"use client";

import { useMemo } from "react";

import getLanguage from "@/app/utils/getlanguage";
import { useLocale } from "@/app/context/use-locale";
import translation from "./locale/index.json";
import { StarIcon } from "@heroicons/react/24/solid";

export interface IOpinions {
  name: string;
  text: string;
  rating: number;
  source: string;
  sourceName: string;
}

export default function OpinionsComponent() {
  const locale = useLocale();
  const content = useMemo(() => getLanguage(translation, locale) ?? {}, [locale]);
  const opinions = useMemo(() => content.opinions ?? [], [content.opinions]);

  return (
    <section className="space-y-8">
      <div className="text-center space-y-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-sky-100 bg-sky-50 px-4 py-1.5 text-sm font-semibold text-sky-700">
          <StarIcon className="h-4 w-4 text-amber-500" />
          {content.badge ?? content.title}
        </span>
        <h2 className="text-3xl font-semibold text-[var(--color-text)] sm:text-4xl">{content.title}</h2>
        <p className="mx-auto max-w-3xl text-base text-[var(--color-muted)]">
          {content.subtitle ?? "Zobacz, jak pacjenci oceniają wizyty w naszym gabinecie."}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {opinions.map((item: IOpinions, key: number) => (
          <article
            key={key}
            className="group h-full rounded-3xl border border-sky-100/60 bg-white/80 p-6 shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-xl"
          >
            <div className="flex items-center justify-between gap-3 border-b border-sky-50 pb-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-sky-600 font-semibold">
                  {item.name[0]}
                </div>
                <div>
                  <p className="text-base font-semibold text-[var(--color-text)]">{item.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-amber-500">
                {(() => {
                  const normalizedRating = Math.max(0, Math.min(5, Math.round(item.rating)));

                  return Array.from({ length: 5 }).map((_, index) => (
                    <StarIcon key={index} className={`h-4 w-4 ${index + 1 <= normalizedRating ? "" : "opacity-30"}`} />
                  ));
                })()}
              </div>
            </div>
            <p className="mt-4 text-base leading-7 text-[var(--color-muted)]">“{item.text}”</p>
            <div className="mt-6 flex flex-col gap-1">
              <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
                {content.source}: {item.sourceName}
              </span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
